import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

/** Portal-wrapped Tooltip content. The Portal is critical: Radix renders
 *  `Tooltip.Content` inside the trigger's DOM subtree by default, which means
 *  CSS like `text-transform: uppercase` / `letter-spacing` / `text-align`
 *  applied to ancestors (e.g. `<th>` cells, headers) leaks into the tooltip
 *  text. Portaling to `document.body` cuts that inheritance chain, and the
 *  explicit `normal-case tracking-normal text-left` classes here guarantee
 *  the tooltip always renders as small left-aligned prose regardless of
 *  where it was triggered from. */
export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 max-w-xs overflow-hidden rounded-md border border-border bg-popover px-3 py-1.5 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-popover-foreground shadow-md',
        'data-[state=delayed-open]:data-[side=bottom]:slide-in-from-top-2 data-[state=delayed-open]:data-[side=left]:slide-in-from-right-2 data-[state=delayed-open]:data-[side=right]:slide-in-from-left-2 data-[state=delayed-open]:data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
